'use client';

import React, { useCallback, useEffect, useState } from 'react';
import type { SeatMapDef, ComputedSeat, SeatDef, SeatIcon } from '@/lib/seat-map-utils';
import { computeAllSeats, SEAT_ICONS } from '@/lib/seat-map-utils';
import { X, Info, ShoppingCart } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { fetchAPI } from '@/core/api/client';
import { addSeatsToCart, readCart, requestSeatHold } from '@/lib/cart';
import { getPublicMediaUrl } from '@/core/utils/media';
import { toast } from '@/core/utils/toast';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

// Mirrors MAX_HOLDS_PER_SESSION in backend/shop/routers/tickets_router.py.
const MAX_HOLDS_PER_SESSION = 8;

// ─── Types ────────────────────────────────────────────────────────────────────

interface SeatAvailability {
    seat_map: SeatMapDef;
    booked_seat_ids: string[];
    held_seat_ids: string[];
    sections: Array<{ name: string; price: number; color?: string; open_choice?: boolean }>;
}

interface SelectedSeat {
    seat_id: string;
    section: string;
    label: string;
    rowLabel: string;
    seatLabel: string;
    price: number;
}

interface SeatMapPickerSettings {
    freeColor?: string;
    takenColor?: string;
    heldColor?: string;
    selectedColor?: string;
    legendPosition?: 'top' | 'bottom';
    legendAlign?: 'left' | 'center' | 'right';
    seatLabelSize?: number;
    seatLabelColor?: string;
}

interface Props {
    productId: number;
    eventId: number;
    sessionToken: string;
    product: { id: number; name: string; slug: string; images?: any[] };
    eventLabel: string;
    onSelectionChange?: (seats: SelectedSeat[]) => void;
    maxSeats?: number;
    settings?: SeatMapPickerSettings;
}

const SECTION_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

function getSectionColor(sectionName: string, sections: SeatAvailability['sections'], idx: number): string {
    const sec = sections.find(s => s.name === sectionName);
    if (sec?.color) return sec.color;
    return SECTION_COLORS[idx % SECTION_COLORS.length];
}

// ─── Seat Icon SVG (read-only) ────────────────────────────────────────────────

function SeatShape({ icon, color, size, opacity = 1 }: {
    icon: SeatIcon; color: string; size: number; opacity?: number;
}) {
    if (icon === 'blocked') {
        return (
            <g opacity={opacity}>
                <rect x={0} y={0} width={size} height={size} rx={3} fill="#e5e7eb" />
                <line x1={4} y1={4} x2={size - 4} y2={size - 4} stroke="#9ca3af" strokeWidth={2} />
                <line x1={size - 4} y1={4} x2={4} y2={size - 4} stroke="#9ca3af" strokeWidth={2} />
            </g>
        );
    }
    const path = SEAT_ICONS[icon];
    if (icon === 'square' || !path) {
        return (
            <rect x={0} y={0} width={size} height={size} rx={3}
                fill={color} opacity={opacity} />
        );
    }
    return (
        <g transform={`scale(${size / 32})`} opacity={opacity}>
            <path d={path} fill={color} />
        </g>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// Seats on the phone stay at least this many CSS px wide: the plan scrolls sideways instead of
// shrinking to the screen (a 23-seat row on a 390 px phone used to give ~13 px seats).
const MIN_SEAT_PX = 26;

export default function SeatMapPicker({ productId, eventId, sessionToken, product, eventLabel, onSelectionChange, maxSeats = 10, settings }: Props) {
    const { t, locale } = useTranslation();
    const seatLabelText = (row: string, seat: string) =>
        t('seatmap.seat_label', 'Rząd {row}, miejsce {seat}').replace('{row}', row).replace('{seat}', seat);
    const [data, setData] = useState<SeatAvailability | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<SelectedSeat[]>([]);
    const [added, setAdded] = useState(false);
    const [tooltip, setTooltip] = useState<{
        x: number;
        y: number;
        text: string;
        placement: 'top' | 'bottom';
        boxWidth: number;
    } | null>(null);
    const [openChoiceSeat, setOpenChoiceSeat] = useState<ComputedSeat | null>(null);
    const [cartSeatIds, setCartSeatIds] = useState<Set<string>>(new Set());
    const [addingToCart, setAddingToCart] = useState(false);
    const freeColor = settings?.freeColor || '#94a3b8';
    const takenColor = settings?.takenColor || '#d1d5db';
    const heldColor = settings?.heldColor || '#fbbf24';
    const selectedColor = settings?.selectedColor || '#7c3aed';
    const legendPosition = settings?.legendPosition || 'bottom';
    const legendJustify = settings?.legendAlign === 'center' ? 'justify-center'
        : settings?.legendAlign === 'right' ? 'justify-end' : 'justify-start';

    useEffect(() => {
        async function load() {
            try {
                const result: SeatAvailability = await fetchAPI(
                    `/products/${productId}/events/${eventId}/seats?session_token=${encodeURIComponent(sessionToken)}`
                );
                setData(result);
            } catch {
                // silently fail — product page still usable without seat map
            }
            setLoading(false);
        }
        load();
    }, [productId, eventId, sessionToken]);

    const refreshAvailability = useCallback(async () => {
        try {
            const fresh: SeatAvailability = await fetchAPI(
                `/products/${productId}/events/${eventId}/seats?session_token=${encodeURIComponent(sessionToken)}`
            );
            setData(prev => prev ? { ...prev, booked_seat_ids: fresh.booked_seat_ids, held_seat_ids: fresh.held_seat_ids } : prev);
        } catch {
            // ignore transient polling errors
        }
    }, [productId, eventId, sessionToken]);

    // Poll for other shoppers' seat holds/purchases so the map updates live
    // without a refresh. The endpoint already excludes this session's own
    // holds via session_token, so only *other* people's activity shows up.
    useEffect(() => {
        const interval = setInterval(refreshAvailability, 4000);
        return () => clearInterval(interval);
    }, [refreshAvailability]);

    // A hold the backend refused (someone else got the seat first, or the per-session limit)
    // means the seat is NOT ours — undo the optimistic pick instead of letting it reach the cart.
    function onHoldRefused(seatId: string) {
        setSelected(prev => prev.filter(s => s.seat_id !== seatId));
        setOpenChoiceSeat(prev => (prev?.seat.id === seatId ? null : prev));
        toast.error(t('seatmap.hold_taken', 'To miejsce właśnie zajął ktoś inny — wybierz inne.'));
        refreshAvailability();
    }

    // Best-effort release of held-but-not-cart-added seats when the tab closes.
    useEffect(() => {
        function releaseOnLeave() {
            selected.forEach(s => {
                fetch('/api/seats/hold', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ event_details_id: eventId, seat_id: s.seat_id, session_token: sessionToken }),
                    keepalive: true,
                }).catch(() => {});
            });
        }
        window.addEventListener('pagehide', releaseOnLeave);
        return () => window.removeEventListener('pagehide', releaseOnLeave);
    }, [selected, eventId, sessionToken]);

    useEffect(() => {
        onSelectionChange?.(selected);
    }, [selected, onSelectionChange]);

    // The seats API excludes this session's own holds from held_seat_ids (so the customer
    // can still interact with/deselect their own in-progress picks), but that meant a seat
    // just added to the cart had nothing marking it as unavailable for THIS shopper — it
    // looked free again and could be "added" a second time (harmless since the cart itself
    // dedupes by seat, but confusing: looked like the first add silently failed). Read the
    // actual cart to know which seats for this event are already owned by this browser.
    useEffect(() => {
        function refreshCartSeats() {
            const now = Date.now();
            const ids = readCart()
                .filter((item: any) =>
                    item.id === productId &&
                    item.event_details_id === eventId &&
                    item.seat_id &&
                    (!item.held_until || item.held_until > now)
                )
                .map((item: any) => item.seat_id as string);
            setCartSeatIds(new Set(ids));
        }
        refreshCartSeats();
        window.addEventListener('cart-updated', refreshCartSeats);
        return () => window.removeEventListener('cart-updated', refreshCartSeats);
    }, [productId, eventId]);

    const allSeats = data ? computeAllSeats(data.seat_map) : [];
    const bookedSet = new Set(data?.booked_seat_ids ?? []);
    const heldSet = new Set(data?.held_seat_ids ?? []);
    const selectedSet = new Set(selected.map(s => s.seat_id));

    function getSeatState(seatId: string): 'free' | 'booked' | 'held' | 'selected' | 'mine' {
        if (selectedSet.has(seatId)) return 'selected';
        if (bookedSet.has(seatId)) return 'booked';
        if (cartSeatIds.has(seatId)) return 'mine';
        if (heldSet.has(seatId)) return 'held';
        return 'free';
    }

    function handleSeatClick(cs: ComputedSeat, e: React.MouseEvent) {
        e.stopPropagation();
        if (!data) return;
        const { seat } = cs;
        const state = getSeatState(seat.id);

        if (state === 'booked' || state === 'held' || state === 'mine') return;
        if (seat.status === 'blocked') return;

        if (state === 'selected') {
            // Deselect
            setSelected(prev => prev.filter(s => s.seat_id !== seat.id));
            // Release hold
            fetchAPI('/seats/hold', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ event_details_id: eventId, seat_id: seat.id, session_token: sessionToken }),
            }).catch(() => {});
            return;
        }

        if (selected.length >= maxSeats) return;
        // The backend caps live holds per session (cart seats included) — say so up front
        // instead of letting the extra pick fail.
        if (selected.length + cartSeatIds.size >= MAX_HOLDS_PER_SESSION) {
            toast.error(t('seatmap.hold_limit', 'Możesz zarezerwować naraz maksymalnie {n} miejsc.').replace('{n}', String(MAX_HOLDS_PER_SESSION)));
            return;
        }

        if (!seat.section) {
            // Open seat — reserve it, then let the customer pick a price tier.
            requestSeatHold(eventId, seat.id, sessionToken).then(r => { if (r === 'taken') onHoldRefused(seat.id); });
            setOpenChoiceSeat(cs);
            return;
        }

        const sectionInfo = data.sections.find(s => s.name === seat.section);
        const price = sectionInfo?.price ?? 0;

        setSelected(prev => [
            ...prev,
            { seat_id: seat.id, section: seat.section!, label: seatLabelText(cs.row.label, seat.label), rowLabel: cs.row.label, seatLabel: seat.label, price },
        ]);

        // Hold seat
        requestSeatHold(eventId, seat.id, sessionToken).then(r => { if (r === 'taken') onHoldRefused(seat.id); });
    }

    function chooseOpenTier(tierName: string, price: number) {
        if (!openChoiceSeat) return;
        const { seat, row } = openChoiceSeat;
        setSelected(prev => [
            ...prev,
            { seat_id: seat.id, section: tierName, label: seatLabelText(row.label, seat.label), rowLabel: row.label, seatLabel: seat.label, price },
        ]);
        setOpenChoiceSeat(null);
    }

    function cancelOpenChoice() {
        if (!openChoiceSeat) return;
        fetchAPI('/seats/hold', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_details_id: eventId, seat_id: openChoiceSeat.seat.id, session_token: sessionToken }),
        }).catch(() => {});
        setOpenChoiceSeat(null);
    }

    async function handleAddToCart() {
        if (!selected.length || addingToCart) return;
        const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
        const imageUrl = mainImage ? getPublicMediaUrl(mainImage.path) : null;
        setAddingToCart(true);
        // Extend the hold to a longer, cart-appropriate duration now that the customer has
        // committed to these seats — and only cart the seats the backend confirms as ours.
        const picks = selected;
        const results = await Promise.all(picks.map(s => requestSeatHold(eventId, s.seat_id, sessionToken, true)));
        setAddingToCart(false);
        const confirmed = picks.filter((_, i) => results[i] !== 'taken');
        const refused = picks.filter((_, i) => results[i] === 'taken');
        if (refused.length) {
            setSelected(prev => prev.filter(s => !refused.some(r => r.seat_id === s.seat_id)));
            toast.error(t('seatmap.hold_taken_some', 'Część miejsc zajął w międzyczasie ktoś inny: {seats}').replace('{seats}', refused.map(r => r.label).join('; ')));
            refreshAvailability();
        }
        if (!confirmed.length) return;
        addSeatsToCart(product, imageUrl, eventId, eventLabel, confirmed);
        if (!refused.length) toast.success(t('product.add_to_cart_success', 'Dodano do koszyka!'));
        setSelected([]);
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
    }

    // ── Legend ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="bg-slate-50 rounded-2xl h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
        );
    }

    if (!data) return null;

    const { seat_map, sections } = data;
    const sectionColorMap = Object.fromEntries(
        sections.map((s, i) => [s.name, getSectionColor(s.name, sections, i)])
    );

    const totalPrice = selected.reduce((sum, s) => sum + s.price, 0);
    const hasAnySectionAssigned = allSeats.some(cs => cs.seat.status !== 'blocked');
    const openChoiceSections = sections.filter(s => s.open_choice);
    const hasBlockedSeats = allSeats.some(cs => cs.seat.status === 'blocked');
    const formatPrice = (price: number) => `${price.toLocaleString(locale || 'pl-PL')} zł`;

    // Crop the view to the actual bounding box of drawn content instead of the
    // nominal canvas size — avoids a mostly-empty viewport when seats only
    // occupy a small part of a large canvas.
    const bbox = (() => {
        const xs: number[] = [];
        const ys: number[] = [];
        allSeats.forEach(cs => {
            xs.push(cs.abs_x - cs.seat.width / 2, cs.abs_x + cs.seat.width / 2);
            ys.push(cs.abs_y - cs.seat.height / 2, cs.abs_y + cs.seat.height / 2);
        });
        seat_map.shapes.forEach(s => { xs.push(s.x, s.x + s.width); ys.push(s.y, s.y + s.height); });
        seat_map.labels.forEach(l => { xs.push(l.x); ys.push(l.y); });
        if (!xs.length) return { minX: 0, minY: 0, w: seat_map.canvas.width, h: seat_map.canvas.height };
        const padSide = 40;
        const padTop = 45;
        const padBottom = 40;
        const minX = Math.min(...xs) - padSide;
        const minY = Math.min(...ys) - padTop;
        const maxX = Math.max(...xs) + padSide;
        const maxY = Math.max(...ys) + padBottom;
        return { minX, minY, w: maxX - minX, h: maxY - minY };
    })();
    const seatSizes = allSeats.map(cs => Math.min(cs.seat.width, cs.seat.height)).filter(n => n > 0);
    const smallestSeat = seatSizes.length ? Math.min(...seatSizes) : 32;
    const mapMinWidth = Math.round(bbox.w * MIN_SEAT_PX / smallestSeat);

    const mapContent = (
        <svg
            className="w-full select-none"
            viewBox={`${bbox.minX} ${bbox.minY} ${bbox.w} ${bbox.h}`}
            style={{ minWidth: mapMinWidth, touchAction: 'manipulation' }}
        >
            <g>
                {/* Background */}
                <rect x={0} y={0} width={seat_map.canvas.width} height={seat_map.canvas.height}
                    fill={seat_map.canvas.background} />

                {/* Shapes */}
                {seat_map.shapes.map(shape => (
                    shape.type === 'rect'
                        ? <rect key={shape.id} x={shape.x} y={shape.y} width={shape.width} height={shape.height}
                            rx={shape.rx} fill={shape.fill} stroke={shape.stroke} strokeWidth={1} />
                        : <ellipse key={shape.id} cx={shape.x + shape.width / 2} cy={shape.y + shape.height / 2}
                            rx={shape.width / 2} ry={shape.height / 2}
                            fill={shape.fill} stroke={shape.stroke} strokeWidth={1} />
                ))}

                {/* Labels */}
                {seat_map.labels.map(lbl => (
                    <text key={lbl.id} x={lbl.x} y={lbl.y}
                        fontSize={lbl.font_size} fill={lbl.color}
                        fontWeight={lbl.bold ? 'bold' : 'normal'}
                        textAnchor="middle">
                        {lbl.text}
                    </text>
                ))}

                {/* Row labels */}
                {seat_map.rows.map(row => {
                    const seats = allSeats.filter(cs => cs.row.id === row.id);
                    if (!seats.length) return null;
                    const first = seats[0];
                    return (
                        <text key={`rl-${row.id}`}
                            x={first.abs_x - 20} y={first.abs_y + 4}
                            fontSize={10} fill="#94a3b8" fontWeight="bold"
                            textAnchor="end" style={{ pointerEvents: 'none' }}>
                            {row.label}
                        </text>
                    );
                })}

                {/* Seats */}
                {allSeats.map(cs => {
                    const { seat, abs_x, abs_y, rotation } = cs;
                    const state = getSeatState(seat.id);
                    const isBlockedSeat = seat.status === 'blocked';
                    const isOpenSeat = !isBlockedSeat && !seat.section && state !== 'selected' && state !== 'mine';
                    const isClickable = state === 'free' || state === 'selected';
                    const w = seat.width;
                    const h = seat.height;

                    let color = '#e5e7eb'; // default blocked
                    let opacity = 1;
                    let strokeColor = 'none';
                    let strokeWidth = 0;

                    if (!isBlockedSeat) {
                        if (isOpenSeat) {
                            color = freeColor;
                        } else if (state === 'selected') {
                            const chosenSection = selected.find(s => s.seat_id === seat.id)?.section ?? seat.section;
                            color = sectionColorMap[chosenSection!] ?? '#6b7280';
                        } else {
                            color = sectionColorMap[seat.section!] ?? '#6b7280';
                        }
                    }

                    if (state === 'booked') { color = takenColor; opacity = 0.6; }
                    else if (state === 'mine') { color = selectedColor; opacity = 0.85; }
                    else if (state === 'held') { color = heldColor; opacity = 0.7; }
                    else if (state === 'selected') {
                        strokeColor = selectedColor;
                        strokeWidth = 2.5;
                    }
                    else if (isBlockedSeat) { opacity = 0.4; }

                    return (
                        <g
                            key={seat.id}
                            transform={`translate(${abs_x - w / 2}, ${abs_y - h / 2}) rotate(${rotation}, ${w / 2}, ${h / 2})`}
                            onClick={isClickable && !isBlockedSeat ? e => handleSeatClick(cs, e) : undefined}
                            style={{ cursor: isClickable && !isBlockedSeat ? 'pointer' : 'not-allowed' }}
                            onMouseEnter={() => {
                                if (isBlockedSeat) return;
                                const sec = sections.find(s => s.name === seat.section);
                                const text = state === 'mine'
                                    ? `${cs.row.label}${seat.label} · ${t('seatmap.tooltip_in_cart', 'już w Twoim koszyku')}`
                                    : isOpenSeat
                                        ? `${cs.row.label}${seat.label} · ${t('seatmap.tooltip_choose', 'wybór typu biletu')}`
                                        : `${cs.row.label}${seat.label} · ${seat.section} · ${formatPrice(sec?.price ?? 0)}`;

                                const boxWidth = Math.max(130, Math.min(260, text.length * 6.8 + 20));
                                const halfWidth = boxWidth / 2;
                                const clampedX = Math.max(bbox.minX + halfWidth + 6, Math.min(bbox.minX + bbox.w - halfWidth - 6, abs_x));

                                // If hovering near top edge (e.g. Row 1), show tooltip below the seat so it never clips under the container border
                                const spaceAbove = (abs_y - h / 2 - 34) - bbox.minY;
                                const showBelow = spaceAbove < 0;
                                const tooltipY = showBelow ? (abs_y + h / 2 + 10) : (abs_y - h / 2 - 10);
                                const placement = showBelow ? 'bottom' : 'top';

                                setTooltip({ x: clampedX, y: tooltipY, text, placement, boxWidth });
                            }}
                            onMouseLeave={() => setTooltip(null)}
                        >
                            <SeatShape icon={seat.icon} color={color} size={Math.min(w, h)} opacity={opacity} />
                            {strokeWidth > 0 && (
                                <rect x={-2} y={-2} width={w + 4} height={h + 4}
                                    rx={4} fill="none" stroke={strokeColor} strokeWidth={strokeWidth} />
                            )}
                            {(state === 'booked' || state === 'held') && (
                                <>
                                    <line x1={4} y1={4} x2={w - 4} y2={h - 4} stroke="#9ca3af" strokeWidth={1.5} />
                                    <line x1={w - 4} y1={4} x2={4} y2={h - 4} stroke="#9ca3af" strokeWidth={1.5} />
                                </>
                            )}
                            {seat.label && state !== 'booked' && (
                                <text x={w / 2} y={h / 2}
                                    dominantBaseline="central"
                                    fontSize={settings?.seatLabelSize ?? seat_map.canvas.seat_label_font_size ?? 9}
                                    fill={isBlockedSeat ? '#9ca3af' : (settings?.seatLabelColor || seat_map.canvas.seat_label_color || '#fff')}
                                    textAnchor="middle" fontWeight="bold"
                                    transform={`rotate(${-rotation}, ${w / 2}, ${h / 2})`}
                                    style={{ pointerEvents: 'none' }}>
                                    {seat.label}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* Tooltip */}
                {tooltip && (
                    <g transform={`translate(${tooltip.x}, ${tooltip.y})`} style={{ pointerEvents: 'none' }}>
                        {tooltip.placement === 'top' ? (
                            <>
                                <rect
                                    x={-tooltip.boxWidth / 2}
                                    y={-26}
                                    width={tooltip.boxWidth}
                                    height={26}
                                    rx={6}
                                    fill="#0f172a"
                                    stroke="#334155"
                                    strokeWidth={1}
                                />
                                <polygon points="-4,-1 4,-1 0,4" fill="#0f172a" />
                                <text
                                    x={0}
                                    y={-13}
                                    fontSize={10.5}
                                    fill="#f8fafc"
                                    fontWeight="600"
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                >
                                    {tooltip.text}
                                </text>
                            </>
                        ) : (
                            <>
                                <rect
                                    x={-tooltip.boxWidth / 2}
                                    y={0}
                                    width={tooltip.boxWidth}
                                    height={26}
                                    rx={6}
                                    fill="#0f172a"
                                    stroke="#334155"
                                    strokeWidth={1}
                                />
                                <polygon points="-4,1 4,1 0,-4" fill="#0f172a" />
                                <text
                                    x={0}
                                    y={13}
                                    fontSize={10.5}
                                    fill="#f8fafc"
                                    fontWeight="600"
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                >
                                    {tooltip.text}
                                </text>
                            </>
                        )}
                    </g>
                )}
            </g>
        </svg>
    );

    const legendEl = (
        <div className={cn('flex flex-wrap gap-4', legendJustify)}>
            {/* Free seats are colored by section (so price tiers read at a glance), not by a
                single "freeColor" swatch — show one legend entry per section instead of a
                catch-all "Wolne" that never matched what was actually on the map. */}
            {sections.map(s => (
                <div key={`sec-${s.name}`} className="flex items-center gap-1.5 text-sm">
                    <span className="w-4 h-4 rounded" style={{ background: sectionColorMap[s.name] }} />
                    <span className="text-slate-500">{s.name} · {formatPrice(s.price)}</span>
                </div>
            ))}
            {openChoiceSections.length > 0 && (
                <div className="flex items-center gap-1.5 text-sm">
                    <span className="w-4 h-4 rounded" style={{ background: freeColor }} />
                    <span className="text-slate-500">{t('seatmap.legend_open', 'Wolne (dowolne miejsce)')}</span>
                </div>
            )}
            <div className="flex items-center gap-1.5 text-sm">
                <span className="w-4 h-4 rounded" style={{ background: heldColor }} />
                <span className="text-slate-500">{t('seatmap.legend_held', 'W trakcie rezerwacji')}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
                <span className="w-4 h-4 rounded" style={{ background: takenColor }} />
                <span className="text-slate-500">{t('seatmap.legend_taken', 'Zajęte')}</span>
            </div>
            {hasBlockedSeats && (
                <div className="flex items-center gap-1.5 text-sm">
                    <span className="w-4 h-4 rounded" style={{ background: '#e5e7eb', opacity: 0.6 }} />
                    <span className="text-slate-500">{t('seatmap.legend_unavailable', 'Niedostępne')}</span>
                </div>
            )}
            <div className="flex items-center gap-1.5 text-sm">
                <span className="w-4 h-4 rounded border-2" style={{ borderColor: selectedColor }} />
                <span className="text-slate-500">{t('seatmap.legend_selected', 'Wybrane')}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
                <span className="w-4 h-4 rounded" style={{ background: selectedColor, opacity: 0.85 }} />
                <span className="text-slate-500">{t('seatmap.legend_mine', 'W Twoim koszyku')}</span>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            {!hasAnySectionAssigned && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
                    {t('seatmap.no_sections', 'Ta sala nie ma jeszcze przypisanych sekcji cenowych do miejsc, więc żadne miejsce nie jest dostępne do wyboru.')}
                </div>
            )}

            {legendPosition === 'top' && legendEl}

            {/* Open-seat tier picker */}
            {openChoiceSeat && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={cancelOpenChoice}>
                    <div className="bg-white rounded-2xl p-5 max-w-sm w-full space-y-3 shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <p className="font-semibold text-slate-800">
                                {seatLabelText(openChoiceSeat.row.label, openChoiceSeat.seat.label)} — {t('seatmap.choose_tier', 'wybierz typ biletu')}
                            </p>
                            <button onClick={cancelOpenChoice} className="text-slate-400 hover:text-red-500 p-1">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="space-y-2">
                            {openChoiceSections.map(sec => (
                                <button
                                    key={sec.name}
                                    type="button"
                                    onClick={() => chooseOpenTier(sec.name, sec.price)}
                                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
                                >
                                    <span className="font-medium text-sm text-slate-800">{sec.name}</span>
                                    <span className="font-semibold text-sm text-purple-700">{formatPrice(sec.price)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Map container */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-x-auto overscroll-x-contain">
                {mapContent}
            </div>

            <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <Info className="h-3 w-3 shrink-0" /> {t('seatmap.click_hint', 'Kliknij wolne miejsce, aby je wybrać')}
            </p>
            {/* Only phones get a plan wider than the screen (see MIN_SEAT_PX). */}
            {mapMinWidth > 360 && <p className="text-xs text-slate-400 -mt-2 sm:hidden">
                {t('seatmap.scroll_hint', 'Przesuń plan palcem w bok, aby zobaczyć całą salę')}
            </p>}

            {legendPosition === 'bottom' && legendEl}

            {/* Selected seats */}
            {selected.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 space-y-3">
                    <p className="font-semibold text-purple-900 text-sm">
                        {t('seatmap.selected_title', 'Wybrane miejsca ({n})').replace('{n}', String(selected.length))}
                    </p>
                    <div className="space-y-2">
                        {selected.map(s => (
                            <div key={s.seat_id} className="flex items-center justify-between bg-white rounded-xl px-3 py-2 border border-purple-100">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full"
                                        style={{ background: sectionColorMap[s.section] ?? '#6b7280' }} />
                                    <span className="font-medium text-sm text-slate-800">{s.label}</span>
                                    <span className="text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{s.section}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-semibold text-sm text-slate-700">
                                        {formatPrice(s.price)}
                                    </span>
                                    <button
                                        onClick={() => {
                                            setSelected(prev => prev.filter(x => x.seat_id !== s.seat_id));
                                            fetchAPI('/seats/hold', {
                                                method: 'DELETE',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ event_details_id: eventId, seat_id: s.seat_id, session_token: sessionToken }),
                                            }).catch(() => {});
                                        }}
                                        className="text-slate-400 hover:text-red-500 transition-colors p-1">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-purple-200">
                        <span className="text-sm font-semibold text-purple-800">{t('seatmap.total', 'Łącznie')}</span>
                        <span className="text-lg font-bold text-purple-900">
                            {formatPrice(totalPrice)}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={handleAddToCart}
                        disabled={addingToCart}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all active:scale-95 shadow-md disabled:opacity-60"
                    >
                        <ShoppingCart className="h-5 w-5" />
                        {added
                            ? t('seatmap.added', 'Dodano ✓')
                            : t('seatmap.add_to_cart', 'Dodaj do koszyka ({price})').replace('{price}', formatPrice(totalPrice))}
                    </button>
                </div>
            )}

        </div>
    );
}
